import { useEffect, useState } from 'react';
import Layout from '../components/layout';
import Title from '../components/title';
import { supabase } from '../supabase';
import './tablePage.css';
import searchIcon from '../assets/search.svg';
import editIcon from '../assets/edit.svg';
import deleteIcon from '../assets/delete.svg';

const PROGRAMS_PER_PAGE = 4;

const Programs = () => {
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [id, setId] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategoryId, setEditCategoryId] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');
  const [editImageFile, setEditImageFile] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const uploadImage = async (file) => {
    if (!file) return null;
    const fileName = `${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from('assets').upload(fileName, file);
    if (error) {
      alert("Image upload failed: " + error.message);
      throw error;
    }
    const { data: { publicUrl } } = supabase.storage.from('assets').getPublicUrl(fileName);
    return publicUrl;
  };

  const getPrograms = async () => {
    const res = await supabase
      .from('programs')
      .select('id,created_at,title_en,description_en,category_id,image_url,categories(name_en)')
      .order('id', { ascending: false });
    setPrograms(res.data || []);
  };

  const getCategories = async () => {
    const res = await supabase.from('categories').select('id,name_en').order('id');
    setCategories(res.data || []);
  };

  useEffect(() => {
    getPrograms();
    getCategories();
  }, []);

  const createProgram = async (e) => {
    e.preventDefault();
    let uploadedUrl = imageUrl;
    if (imageFile) {
      uploadedUrl = await uploadImage(imageFile);
    }
    await supabase.from('programs').insert([{
      title_en: title,
      description_en: description,
      category_id: categoryId ? Number(categoryId) : null,
      image_url: uploadedUrl,
    }]);
    setTitle('');
    setDescription('');
    setCategoryId('');
    setImageUrl('');
    setImageFile(null);
    setIsCreateOpen(false);
    await getPrograms();
  };

  const editProgram = (program) => {
    setId(program.id);
    setEditTitle(program.title_en || '');
    setEditDescription(program.description_en || '');
    setEditCategoryId(program.category_id || '');
    setEditImageUrl(program.image_url || '');
    setEditImageFile(null);
    setIsEditOpen(true);
  };

  const updateProgram = async (e) => {
    e.preventDefault();
    let finalImageUrl = editImageUrl;
    if (editImageFile) {
      finalImageUrl = await uploadImage(editImageFile);
    }
    await supabase
      .from('programs')
      .update({
        title_en: editTitle,
        description_en: editDescription,
        category_id: editCategoryId ? Number(editCategoryId) : null,
        image_url: finalImageUrl,
      })
      .eq('id', id);
    setIsEditOpen(false);
    await getPrograms();
  };

  const deleteProgram = async (programId) => {
    await supabase.from('programs').delete().eq('id', programId);
    await getPrograms();
  };

  const filteredPrograms = programs.filter((program) => {
    const value = search.toLowerCase();
    const category = Array.isArray(program.categories) ? program.categories[0] : program.categories;
    return program.id.toString().includes(value) || program.title_en?.toLowerCase().includes(value) || category?.name_en?.toLowerCase().includes(value);
  });

  const totalPages = Math.max(1, Math.ceil(filteredPrograms.length / PROGRAMS_PER_PAGE));
  const startIndex = (currentPage - 1) * PROGRAMS_PER_PAGE;
  const paginatedPrograms = filteredPrograms.slice(startIndex, startIndex + PROGRAMS_PER_PAGE);

  return (
    <Layout>
      <div className="table_page">
        <Title subtitle="Manage programs" title="Programs" />
        <div className="table_controls_card">
          <label className="table_search" htmlFor="programs-search">
            <img alt="" src={searchIcon} />
            <input
              id="programs-search"
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search programs..."
              type="text"
              value={search}
            />
          </label>
          <div className="table_controls_actions">
            <span>{filteredPrograms.length} total records</span>
            <button className="table_primary_button" onClick={() => setIsCreateOpen(true)} type="button">
              Add Program
            </button>
          </div>
        </div>
        <div className="table_card">
          <div className="table_scroll">
            <table className="data_table">
              <thead><tr><th>ID</th><th>Title</th><th>Category</th><th>Image</th><th>Actions</th></tr></thead>
              <tbody>
                {paginatedPrograms.map((program) => {
                  const category = Array.isArray(program.categories) ? program.categories[0] : program.categories;
                  return (
                    <tr key={program.id}>
                      <td>{program.id}</td>
                      <td>{program.title_en}</td>
                      <td>{category?.name_en}</td>
                      <td>{program.image_url}</td>
                      <td>
                        <div className="table_actions">
                          <button
                            aria-label={`Edit program #${program.id}`}
                            className="table_icon_button"
                            onClick={() => editProgram(program)}
                            type="button"
                          >
                            <img alt="" src={editIcon} />
                          </button>
                          <button
                            aria-label={`Delete program #${program.id}`}
                            className="table_icon_button table_icon_button_danger"
                            onClick={() => deleteProgram(program.id)}
                            type="button"
                          >
                            <img alt="" src={deleteIcon} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="table_pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} type="button">Previous</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} type="button">Next</button>
          </div>
        </div>
        {isCreateOpen && (
          <div
            aria-labelledby="add-program-heading"
            aria-modal="true"
            className="table_modal_overlay"
            onClick={() => setIsCreateOpen(false)}
            role="dialog"
          >
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2 id="add-program-heading">Add Program</h2>
                </div>
                <button
                  aria-label="Close add program form"
                  className="table_modal_close"
                  onClick={() => setIsCreateOpen(false)}
                  type="button"
                >
                  &#x2715;
                </button>
              </div>
              <form className="table_form" onSubmit={createProgram}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Title</span>
                    <input onChange={(e) => setTitle(e.target.value)} placeholder="Title" value={title} />
                  </label>
                  <label className="table_form_field">
                    <span>Category</span>
                    <select onChange={(e) => setCategoryId(e.target.value)} value={categoryId}>
                      <option value="">Select category</option>
                      {categories.map((category) => <option key={category.id} value={category.id}>{category.name_en}</option>)}
                    </select>
                  </label>
                  <label className="table_form_field table_form_field_full">
                    <span>Image URL or Upload</span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input onChange={(e) => setImageUrl(e.target.value)} placeholder="Image URL" value={imageUrl} />
                      <input accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} type="file" />
                    </div>
                  </label>
                  <label className="table_form_field table_form_field_full">
                    <span>Description</span>
                    <textarea onChange={(e) => setDescription(e.target.value)} rows={3} value={description} />
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsCreateOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button" type="submit">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}
        {isEditOpen && (
          <div
            aria-labelledby="edit-program-heading"
            aria-modal="true"
            className="table_modal_overlay"
            onClick={() => setIsEditOpen(false)}
            role="dialog"
          >
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2 id="edit-program-heading">Edit Program</h2>
                </div>
                <button
                  aria-label="Close edit program form"
                  className="table_modal_close"
                  onClick={() => setIsEditOpen(false)}
                  type="button"
                >
                  &#x2715;
                </button>
              </div>
              <form className="table_form" onSubmit={updateProgram}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Title</span>
                    <input onChange={(e) => setEditTitle(e.target.value)} value={editTitle} />
                  </label>
                  <label className="table_form_field">
                    <span>Category</span>
                    <select onChange={(e) => setEditCategoryId(e.target.value)} value={editCategoryId}>
                      <option value="">Select category</option>
                      {categories.map((category) => <option key={category.id} value={category.id}>{category.name_en}</option>)}
                    </select>
                  </label>
                  <label className="table_form_field table_form_field_full">
                    <span>Image URL or Upload</span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input onChange={(e) => setEditImageUrl(e.target.value)} placeholder="Image URL" value={editImageUrl} />
                      <input accept="image/*" onChange={(e) => setEditImageFile(e.target.files[0])} type="file" />
                    </div>
                  </label>
                  <label className="table_form_field table_form_field_full">
                    <span>Description</span>
                    <textarea onChange={(e) => setEditDescription(e.target.value)} rows={3} value={editDescription} />
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsEditOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button" type="submit">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Programs;
